import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Icon,
  Link,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import React, { useContext, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import { RiInboxArchiveLine } from 'react-icons/ri'
import { IKReactSelect } from '../../../ui-kit'
import { colors } from '../../../ui-kit'
import useToast from '../../hooks/useToast'
import type {
  MediaCollectionOption,
  MediaCollection,
  AddToMediaCollectionAssetsSuccessResponse,
  AddToMediaCollectionAssetsErrorResponse,
  MediaCollectionErrorResponse,
} from '../../interfaces'
import { MLContext } from '../../providers'
import { MediaLibraryActions } from '../../providers/MLProvider'
import CreateMediaCollection from './CreateMediaCollection'

interface Props {
  isOpen: boolean
  onClose(): void
  onOpen(): void
}

const AddToMediaCollection = ({
  isOpen,
  onClose,
  onOpen,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const [mediaCollectionList, setMediaCollectionList] = useState<
    MediaCollectionOption[]
  >([])
  const [newMediaCollectionList, setNewMediaCollectionList] = useState<
    MediaCollectionOption[]
  >([])

  const [initialMediaCollectionList, setInitialMediaCollectionList] = useState<
    MediaCollectionOption[]
  >([])
  const [mediaCollectionSelected, setMediaCollectionSelected] = useState<
    MediaCollectionOption[]
  >([])

  const [isLoading, setIsLoading] = useState<boolean | undefined>(true)
  const [isSubmitting, setIsSubmitting] = useState<boolean | undefined>(false)
  const [formError, setFormError] = useState<string>('')
  const [inputCharacters, setInputCharacters] = useState<string>('')
  const { response, request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const [{ selectedFiles, selectedFolders, refetch }, dispatch] =
    useContext(MLContext)

  const assetIds: string[] = [
    ...selectedFiles.map(file => file.fileId),
    ...selectedFolders.map(folder => folder.folderId),
  ]

  const {
    isOpen: isOpenCreateMediaCollectionModal,
    onOpen: onOpenCreateMediaCollectionModal,
    onClose: onCloseCreateMediaCollectionModal,
  } = useDisclosure()

  const filterOption = (candidate, input) => true

  useEffect(() => {
    const setInitialOptionList = async (): Promise<void | undefined> => {
      setMediaCollectionList([])
      setFormError('')
      setIsLoading(true)
      if (isOpen) {
        const mediaCollectionResponseInitial = (await request.get(
          `/api/v2/media-collections?searchQuery=(permission:["MANAGE","CONTRIBUTE"])&limit=25&sort=DESC_CREATED`,
        )) as MediaCollection[] | MediaCollectionErrorResponse

        if (request.error) {
          setFormError(
            (mediaCollectionResponseInitial as MediaCollectionErrorResponse)
              .message ??
              t(
                'something_went_wrong',
                'Internal Error,Please contact us at support@imagekit.io for support.',
              ),
          )
          setIsLoading(false)
          return
        }
        // @ts-ignore
        const initialOptions: MediaCollectionOption[] = [];

        (mediaCollectionResponseInitial as MediaCollection[]).forEach(val => {
          initialOptions.push({
            label: String(val.name),
            value: val.mediaCollectionId,
          })
        })

        setMediaCollectionList(initialOptions)
        setInitialMediaCollectionList(initialOptions)
        setMediaCollectionSelected(newMediaCollectionList)
      }
      setIsLoading(false)
    }
    void setInitialOptionList()
  }, [isOpen])

  useEffect(() => {
    const setOptionList = async (): Promise<void | undefined> => {
      if (selectedFiles.length > 0 || selectedFolders.length > 0) {
        setIsLoading(true)
        setFormError('')

        const input = encodeURIComponent(inputCharacters)
        const mediaCollectionResponse = (await request.get(
          `/api/v2/media-collections?searchQuery=(name:"${input.toString()}" AND permission:["MANAGE","CONTRIBUTE"])&limit=25`,
        )) as MediaCollection[] | MediaCollectionErrorResponse

        if (request.error) {
          setFormError(
            (mediaCollectionResponse as MediaCollectionErrorResponse).message ??
              t(
                'something_went_wrong',
                'Internal Error,Please contact us at support@imagekit.io for support.',
              ),
          )
          setIsLoading(false)
          return
        }

        // @ts-ignore
        const options: MediaCollectionOption[] = [];

        (mediaCollectionResponse as MediaCollection[]).forEach(val => {
          options.push({
            label: String(val.name),
            value: val.mediaCollectionId,
          })
        })

        setMediaCollectionList([
          ...options.filter(
            option =>
              !mediaCollectionSelected.find(ele => ele.value === option.value),
          ),
        ])
        setIsLoading(false)
      }
    }

    const getData = setTimeout(() => {
      if (inputCharacters.trim() === '') {
        setMediaCollectionList([
          ...mediaCollectionSelected,
          ...initialMediaCollectionList.filter(
            option =>
              !mediaCollectionSelected.find(ele => ele.value === option.value),
          ),
        ])
      } else {
        void setOptionList()
      }
    }, 500)

    return () => void clearTimeout(getData)
  }, [inputCharacters])

  const addAssetToCollection = async (): Promise<boolean> => {
    const mediaCollectionIds: string[] = []
    mediaCollectionSelected.forEach(mediaCollection => {
      mediaCollectionIds.push(mediaCollection.value)
    })

    if (mediaCollectionIds.length > 100) {
      setFormError(
        t(
          'media_collection_limit_exceeded',
          'At once you cannnot add asset to more than 100 media collections.',
        ),
      )
      return false
    }
    const res = (await request.post('/api/v2/media-collections/add-assets', {
      mediaCollectionIds,
      assetIds,
    })) as
      | AddToMediaCollectionAssetsSuccessResponse
      | AddToMediaCollectionAssetsErrorResponse

    if (request.error) {
      setFormError(
        (res as AddToMediaCollectionAssetsErrorResponse).message ??
          t(
            'something_went_wrong',
            'Internal Error,Please contact us at support@imagekit.io for support.',
          ),
      )
      return false
    }

    window.trackEvent({
      action: 'Submit',
      category: 'Media collection',
      label: 'Assets added - media collection count',
      value: (res as AddToMediaCollectionAssetsSuccessResponse)
        .successfullyUpdatedMediaCollectionsId.length,
    })

    window.trackEvent({
      action: 'Submit',
      category: 'Media collection',
      label: 'Assets added - asset count',
      value: assetIds.length,
    })

    if (response.status === 207) {
      toast({
        description: t(
          'media_collection_size_exceeded',
          'Some of the media collections were not updated successfully because their \
            size limit exceeded. A media collection can have at most 1,000 assets added to it.',
        ),
        status: 'info',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })

      return true
    }

    let successfulMessage
    if (
      mediaCollectionIds.length === 1 &&
      (res as AddToMediaCollectionAssetsSuccessResponse)
        .successfullyUpdatedMediaCollectionsId.length === 1
    ) {
      const collectionName = mediaCollectionSelected[0].label
      successfulMessage = (
        <Trans t={t} i18nKey="collection_added_1">
          Asset(s) added succesfully to {{ collectionName }}.
        </Trans>
      )
    } else if (
      mediaCollectionIds.length === 2 &&
      (res as AddToMediaCollectionAssetsSuccessResponse)
        .successfullyUpdatedMediaCollectionsId.length === 2
    ) {
      const collectionName1 = mediaCollectionSelected[0].label
      const collectionName2 = mediaCollectionSelected[1].label

      successfulMessage = (
        <Trans t={t} i18nKey="collection_added_2">
          Asset(s) added succesfully to {{ collectionName1 }} and{' '}
          {{ collectionName2 }}.
        </Trans>
      )
    } else if (
      mediaCollectionIds.length > 2 &&
      (res as AddToMediaCollectionAssetsSuccessResponse)
        .successfullyUpdatedMediaCollectionsId.length > 2
    ) {
      const collectionName1 = mediaCollectionSelected[0].label
      const collectionName2 = mediaCollectionSelected[1].label
      const remainingSize = (
        (res as AddToMediaCollectionAssetsSuccessResponse)
          .successfullyUpdatedMediaCollectionsId.length - 2
      ).toString()

      successfulMessage = (
        <Trans t={t} i18nKey="collection_added_multi">
          Asset(s) added succesfully to {{ collectionName1 }},{' '}
          {{ collectionName2 }} and {{ remainingSize }} other media
          collection(s).
        </Trans>
      )
    } else {
      successfulMessage = (
        <Trans t={t} i18nKey="media_collections_updated_successfully">
          Media collection(s) updated succesfully.
        </Trans>
      )
    }

    toast({
      description: successfulMessage,
      status: 'success',
      isClosable: true,
      duration: 5000,
      position: 'top-right',
      variant: 'outline',
    })
    return true
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          onClose()
          setFormError('')
          setNewMediaCollectionList([])
          setMediaCollectionSelected([])
        }}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Icon
              as={RiInboxArchiveLine}
              color="appBattleshipGrey"
              backgroundColor="appPaleGrey"
              boxSize={6}
              padding="0.1em"
              borderRadius="full"
              marginRight="0.5em"
            />
            {t(
              'add_asset_to_media_collections',
              'Add assets to media collections',
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {formError ? (
              <Alert mb="12px" status="error">
                <AlertIcon />
                <AlertDescription>
                  <Text lineHeight="1.2em">{formError}</Text>
                </AlertDescription>
              </Alert>
            ) : null}
            <Link
              fontSize="1.000rem"
              onClick={() => {
                onClose()
                setFormError('')
                onOpenCreateMediaCollectionModal()
              }}
            >
              {t('create_new_collection', 'Create new media-collection')}
            </Link>
            <IKReactSelect
              variant="compact"
              captureMenuScroll={false}
              menuPlacement="auto"
              isClearable
              isSearchable
              isMulti={true}
              options={mediaCollectionList}
              closeMenuOnSelect={false}
              filterOption={filterOption}
              isLoading={isLoading}
              stylesVariant="media_collection_modal_custom"
              value={mediaCollectionSelected}
              menuPosition="fixed"
              menuShouldScrollIntoView
              placeholder={t(
                'tyoe_media_collection_name',
                'type name of media-collection',
              )}
              onChange={newVal => {
                setMediaCollectionSelected(newVal)
              }}
              onInputChange={(inputChar, { action }) => {
                if (action === 'menu-close') {
                  if (inputChar.toString() === '') {
                    setMediaCollectionList([
                      ...mediaCollectionSelected,
                      ...initialMediaCollectionList.filter(
                        option =>
                          !mediaCollectionSelected.find(
                            ele => ele.value === option.value,
                          ),
                      ),
                    ])
                  } else {
                    setMediaCollectionList(mediaCollectionSelected)
                  }
                }
                setInputCharacters(inputChar)
              }}
            />
          </ModalBody>
          <ModalFooter
            borderTop={`1px solid ${colors.appGrey}`}
            style={{ gap: '1em' }}
          >
            <Button
              onClick={() => {
                onClose()
                setFormError('')
                setNewMediaCollectionList([])
                setMediaCollectionSelected([])
              }}
              variant="outline"
            >
              {t('cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              onClick={async () => {
                setIsSubmitting(true)
                const flag = await addAssetToCollection()     
                if (flag) {
                  dispatch({
                    type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
                  })
                  await new Promise(resolve => setTimeout(resolve, 1500))
                  onClose()
                  await refetch()
                }
                setIsSubmitting(false)
                setNewMediaCollectionList([])
                setMediaCollectionSelected([])
              }}
              isDisabled={mediaCollectionSelected.length === 0}
              isLoading={isSubmitting}
            >
              {t('submit', 'Submit')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <CreateMediaCollection
        isOpen={isOpenCreateMediaCollectionModal}
        onClose={onCloseCreateMediaCollectionModal}
        setMediaCollection={setNewMediaCollectionList}
        onOpen={onOpen}
      />
    </>
  )
}

export default AddToMediaCollection
